import { Component, Input, OnDestroy, OnInit, ViewChild, Inject } from '@angular/core';
import { MatDialog } from '@angular/material';
import { SearchService } from '../../shared/search.service';
import { Team } from '../team.model';
import { TeamEditComponent } from '../team-edit/team-edit.component';
import { TeamService } from '../team.service';
import { TreeModel } from '../tree.model';
import { User } from '../../users/user.model';
import { UserDetailsComponent } from '../../users/user-details/user-details.component';
import { UserProfileService } from '../../userprofile/userprofile.service';
import { UserProfile } from '../../userprofile/userprofile.model';
import { UserService } from '../../users/user.service';
import { TreeModule, TreeNode, Tree } from 'primeng/primeng'

@Component({
    selector: 'app-team',
    templateUrl: './team.component.html',
    styleUrls: ['./team.component.css']
})
export class TeamComponent implements OnInit, OnDestroy {
    public isLoading: boolean;
    public isAdmin = false;
    public isProcessor = false;
    public userProfile = new UserProfile();
    private selectedUser: User;
    private searchSubscription: any;
    private searchClearSubscription: any;
    private userAddedSubscription: any;
    private userUpdatedSubscription: any;
    private userDeletedSubscription: any;
    private userAuthenticatedSubscription: any;

    @ViewChild("teamTree")
    private teamTree: Tree;

    @Input()
    public team: Team;

    @Input()
    public searchText: string;

    public processors: User[] = [];
    public teamTreeNodes: TreeNode[] = [];

    constructor(
        private userService: UserService,
        private userProfileService: UserProfileService,
        private teamService: TeamService,
        private searchService: SearchService,
        public dialog: MatDialog) { }

    ngOnInit() {
        this.populateTeamTree();

        this.userAddedSubscription = this.userService.userAdded
            .subscribe((user: User) => {
                // If a user is added to this team then refresh team and team tree
                if (user.teamIds.find(x => x === this.team.id)) {
                    this.getTeam(this.team.id);
                    this.populateTeamTree();
                }
            });

        this.userDeletedSubscription = this.userService.userDeleted
            .subscribe((userId: string) => {
                // TODO: this should recursively look for the is and only refresh if found
                this.getTeam(this.team.id);
                this.populateTeamTree();
            });

        this.userUpdatedSubscription = this.userService.userUpdated
            .subscribe((user: User) => {
                // TODO: this should recursively look for the is and only refresh if found
                this.getTeam(this.team.id);
                this.populateTeamTree();
            });

        this.searchSubscription = this.searchService.search
            .subscribe(searchText => this.onSearch(searchText));

        this.searchClearSubscription = this.searchService.clearSearch
            .subscribe(item => this.onClearSearch());

        this.isAdmin = (this.userService.currentUser) ? this.userService.currentUser.isAdmin : false;
        this.userAuthenticatedSubscription = this.userService.currentUserAuthenticated.subscribe((currentUser: User) => {
            this.isAdmin = currentUser.isAdmin;
        });
    }

    ngOnDestroy() {
        if (this.searchSubscription)
            this.searchSubscription.unsubscribe();
        if (this.searchClearSubscription)
            this.searchClearSubscription.unsubscribe();
        if (this.userAddedSubscription)
            this.userAddedSubscription.unsubscribe();
        if (this.userDeletedSubscription)
            this.userDeletedSubscription.unsubscribe();
        if (this.userUpdatedSubscription)
            this.userUpdatedSubscription.unsubscribe();
        if (this.userAuthenticatedSubscription)
            this.userAuthenticatedSubscription.unsubscribe();
    }

    private onSearch(searchText: string, reset: boolean = true) {
        this.searchText = searchText;

        if (reset) {
            this.resetHighlights();
            this.collapseAll();
        }

        //do new search
        this.teamTree.value.forEach((node: TreeNode) => {
            this.searchAndExpand(node, searchText);
        });
    }

    private expandRecursive(node: TreeNode, isExpand: boolean) {
        node.expanded = isExpand;
        if (node.children) {
            node.children.forEach(childNode => {
                this.expandRecursive(childNode, isExpand);
            });
        }
    }

    private expandUp(node: TreeNode) {
        node.expanded = true;
        if (node.parent)
            this.expandUp(node.parent);
    }

    private highlightRecursive(node: TreeNode, isHighlighted: boolean) {
        node.data.highlight = isHighlighted;
        if (node.children) {
            node.children.forEach(childNode => {
                this.highlightRecursive(childNode, isHighlighted);
            });
        }
    }

    private searchAndExpand(node: TreeNode, searchText: string) {
        if (node.label.toLowerCase().indexOf(searchText.toLowerCase()) >= 0) {
            node.data.highlight = true;
            this.expandUp(node);
        }
        if (node.children) {
            node.children.forEach(childNode => {
                this.searchAndExpand(childNode, searchText);
            });
        }
    }

    private onClearSearch() {
        this.searchText = null;
        this.resetHighlights();
        this.collapseAll();
    }

    private collapseAll() {
        this.teamTree.value.forEach((node: TreeNode) => {
            this.expandRecursive(node, false);
        });
    }

    private resetHighlights() {
        this.teamTree.value.forEach((node: TreeNode) => {
            this.highlightRecursive(node, false);
        });
    }

    private openUserDetails(node: TreeNode) {
        if (node.data.type === 'user') {
            this.userService.getUser(node.data.id)
                .then(result => {
                    this.selectedUser = result;
                    this.dialog.open(UserDetailsComponent, {
                        data: this.selectedUser,
                        width: '800px',
                        height: '600px'
                    });
                })
                .catch(error => console.log(error));
        }
        else if (node.data.type === 'persona') {
        }
    }

    private openTeamEditDialog(team: Team) {
        this.dialog.open(TeamEditComponent, {
            data: team,
            disableClose: true
        });
    }

    private getTeam(id: string) {
        this.teamService.getTeam(id)
            .then(response => this.team = response)
            .catch(error => console.log("There was an error processing your request", error));
    }

    private deleteTeam(team: Team) {
        if (confirm('Are you sure you want to delete ' + team.name + '?')) {
            this.teamService.deleteTeam(team.id)
                .then(success => {
                    if (!success)
                        confirm('There was an error processing your request.');
                })
                .catch(error => {
                    confirm('There was an error processing your request.');
                });
        }
    }

    private populateTeamTree() {
        this.isLoading = true;
        this.teamService.getTeamTree(this.team.id)
            .then(result => {
                if (result.children.length > 0) {
                    this.teamTreeNodes = [];
                    this.buildTreeNode(result, this.teamTreeNodes);

                    //HACK: Need to wait until the tree component redraws,
                    //need a bettr way than a timer
                    if (this.searchText) {
                        setInterval(() => {
                            this.onSearch(this.searchText, false);
                        }, 100);
                    }
                }
                else {
                    this.teamTreeNodes = [{ label: "No partners found.", children: null, data: {} }];
                }

                this.isLoading = false;
            })
            .catch(error => {
                console.error(error);
                this.teamTreeNodes = [{ label: "No partners found.", children: null, data: {} }];
                this.isLoading = false;
            });
    }

    private buildTreeNode(src: TreeModel, dest: TreeNode[]) {
        // copy the root children to root TreeNodes, then recursively copy the children
        for (let child of src.children) {
            var treeNode: TreeNode = this.createTreeNode(child, null);
            this.copyTreesToTreeNode(child.children, treeNode);
            dest.push(treeNode);
        }
    }

    private copyTreesToTreeNode(src: TreeModel[], dest: TreeNode) {
        for (let srcTree of src) {
            var destNewNode: TreeNode = this.createTreeNode(srcTree, dest);
            dest.children.push(destNewNode);

            var grandChildren = srcTree.children;
            if (!grandChildren) continue;
            this.copyTreesToTreeNode(grandChildren, destNewNode);
        }
    }

    private createTreeNode(src: TreeModel, parent: TreeNode): TreeNode {
        var dest: TreeNode = {
            label: src.name,
            children: [],
            parent: parent,
            expanded: false,
            data: {
                id: src.id,
                type: src.type,
                isTeamLead: src.isTeamLead,
                isUnavailable: src.isUnavailable,
                highlight: false
            }
        };
        return dest;
    }

}



// WEBPACK FOOTER //
// D:/a/1/s/Uhm.Loops.Web/src/app/teams/team/team.component.ts